import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import AlertComponent from '../../../components/stories/molecules/alerts'
import { buttonVariantOptions } from '../../../components/stories/atoms/button/constants'
import { alertVariantComponentOptions } from '../../../components/stories/molecules/alerts/constants'
import { useCreateFilterMutation } from '../../../react-query/filters/mutation/use-create-filter-mutation'
import { getUserId } from '../../../react-query/authentication/query/use-me-query'
import { useGetTaskListQuery } from '../../../react-query/tasks/query/use-get-task-list-query'

const alertStepOptions = {
  CHECK: 'check',
  OPEN_TASKS: 'open-tasks'
}

const LeaveProjectAlert = ({ projectId, onClose, onConfirm }) => {
  const { formatMessage } = useIntl()
  const userId = getUserId()
  const [alertStep, setAlertStep] = useState(alertStepOptions.CHECK)
  const [filterId, setFilterId] = useState(null)
  const [hasLoadedOpenTasksCheck, setHasLoadedOpenTasksCheck] = useState(false)

  const { mutateAsync: createFilter, isPending } = useCreateFilterMutation()

  const { data, isLoading } = useGetTaskListQuery({
    projectId,
    filterId,
    size: 1,
    options: {
      enabled: !!projectId && !!filterId
    }
  })

  useEffect(() => {
    setAlertStep(alertStepOptions.CHECK)
    setFilterId(null)
    setHasLoadedOpenTasksCheck(false)
  }, [projectId])

  useEffect(() => {
    if (!userId || !projectId) {
      setHasLoadedOpenTasksCheck(true)
      return undefined
    }

    let isMounted = true

    const createOpenTasksFilter = async () => {
      const openTasksFilter = {
        entity: 'Tasks',
        projectId,
        conjunction: 'All',
        predicates: [
          {
            attribute: 'TaskStatus2',
            operator: 'Is',
            values: ['Open'],
            valueType: 'TASK_STATUS'
          },
          {
            attribute: 'TaskAssignee',
            operator: 'Is',
            values: [userId],
            valueType: 'ASSIGNEE'
          }
        ]
      }

      try {
        const filter = await createFilter({ filter: openTasksFilter })

        if (isMounted) {
          setFilterId(filter?.id ?? null)
          setHasLoadedOpenTasksCheck(true)
        }
      } catch {
        if (isMounted) {
          setFilterId(null)
          setHasLoadedOpenTasksCheck(true)
        }
      }
    }

    createOpenTasksFilter()

    return () => {
      isMounted = false
    }
  }, [createFilter, projectId, userId])

  const hasOpenTasks = Number(data?.totalElements ?? 0) > 0
  const isCheckingOpenTasks =
    alertStep === alertStepOptions.CHECK &&
    !!projectId &&
    (!hasLoadedOpenTasksCheck || isPending || (!!filterId && isLoading))

  const handleLeaveProject = () => {
    onConfirm(projectId)
    onClose()
  }

  const handlePrimaryAction = () => {
    handleLeaveProject()
  }

  const description =
    alertStep === alertStepOptions.OPEN_TASKS
      ? formatMessage({ id: 'leave_project_open_tasks' })
      : formatMessage({ id: 'leave_project_description' })

  useEffect(() => {
    if (alertStep === alertStepOptions.CHECK && hasOpenTasks) {
      setAlertStep(alertStepOptions.OPEN_TASKS)
    }
  }, [hasOpenTasks])

  return (
    <AlertComponent
      activeOverlay={true}
      variant={alertVariantComponentOptions.DANGER}
      title={formatMessage({ id: 'projects_leaveProject' })}
      description={description}
      onClickOutside={onClose}
      isLoading={isCheckingOpenTasks}
      actionButtons={[
        {
          variant: buttonVariantOptions['TERTIARY-HOLLOW'],
          onClick: onClose,
          children: formatMessage({ id: 'cancel' }),
          fullWidth: true
        },
        {
          variant: buttonVariantOptions['PRIMARY-DESTRUCTIVE'],
          onClick: handlePrimaryAction,
          children: formatMessage({ id: 'projects_leaveProject' }),
          isLoading: isCheckingOpenTasks,
          disabled: isCheckingOpenTasks,
          fullWidth: true
        }
      ]}
    />
  )
}

LeaveProjectAlert.propTypes = {
  projectId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
}

export default LeaveProjectAlert
